import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyOTP,resendOtp } from "../../api/AuthServices";
import { toast } from "sonner";

const OTP_LENGTH = 6;

const VerifyOtp: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const username = location.state?.username;
  const password = location.state?.password;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(60);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    const nextInput = document.getElementById(`otp-${index + 1}`);
    if (value && nextInput) (nextInput as HTMLInputElement).focus();
  };

  if (!email || !username || !password) {
    alert("Missing user details. Please register again.");
    return;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const enteredOtp = otp.join("");

    if (enteredOtp.length < OTP_LENGTH) {
      alert("Please enter the full 6-digit OTP.");
      return;
    }

    if (!email || !username || !password) {
      alert("Missing registration details.");
      return;
    }

    try {
      const res = await verifyOTP({
        email,
        otp: enteredOtp,
        username,
        password,
      });

      toast.success("OTP verified successfully.");
      toast.success("Registration successful.You can now login.");
      navigate("/");
    } catch (error: any) {
      console.error("OTP verification failed", error);
      alert(
        error.response?.data?.non_field_errors?.[0] ||
          "OTP verification failed."
      );
    }
  };

  const handleResend = async () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setResendTimer(60);

    try {
      await resendOtp(email);
      toast.success("OTP resent successfully.");
    } catch (error: any) {
      console.error("Failed to resend OTP", error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4">
      <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
        {/* Branding */}
        <div className="text-center">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            FleeBy
          </h1>
          <p className="text-xs text-slate-500 tracking-wide uppercase font-medium mt-1">
            Online Shopping
          </p>
        </div>

        {/* Heading */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-semibold text-slate-800">
            Enter Verification Code
          </h2>
          <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto rounded-full"></div>
          <p className="text-slate-600 text-sm leading-relaxed">
            Please enter the 6-digit code sent to your email.
          </p>
        </div>

        {/* OTP Inputs */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center gap-3">
            {otp.map((digit, i) => (
              <input
                title="Enter OTP"
                key={i}
                id={`otp-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                className="w-12 h-14 text-center text-xl font-semibold border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all duration-200 bg-slate-50 focus:bg-white shadow-sm"
              />
            ))}
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Verify Code
          </button>
        </form>

        {/* Resend OTP */}
        <div className="text-center">
          {resendTimer > 0 ? (
            <p className="text-sm text-slate-600">
              Resend code in{" "}
              <span className="font-semibold text-slate-800 bg-slate-100 px-2 py-1 rounded-md">
                {resendTimer}s
              </span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm hover:underline transition-colors duration-200"
            >
              Resend Verification Code
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
